import classnames from 'classnames';
import React, { ReactElement, ReactNode } from 'react';
import './Grid.pcss';

type BasicProps = {
  className?: string;
  children: any;
};

type EnhancedProps = BasicProps & {
  onClick?: (...params: any[]) => any;
};

type GridCellProps = EnhancedProps & {
  fr?: number;
  gridColumn?: string;
  overflowable?: boolean;
};

type GridRowProps = EnhancedProps & {
  gridTemplateColumns?: string | null;
};

type GridLabelProps = BasicProps & {
  gridTemplateColumns?: string | null;
};

type GetGridColumnsStyle = (
  children: ReactNode,
  gridTemplateColumnsOverride?: string | null,
) => { gridTemplateColumns?: string };

const getGridColumnsStyle: GetGridColumnsStyle = (
  children,
  gridTemplateColumnsOverride,
) => {
  if (gridTemplateColumnsOverride === null) {
    return {};
  }
  if (gridTemplateColumnsOverride) {
    return {
      gridTemplateColumns: gridTemplateColumnsOverride,
    };
  }
  const childs = React.Children.toArray(children) as ReactElement<any>[];
  const gridTemplateColumns = childs.reduce((style, { props }) => {
    let fr = 1;
    if (props.fr !== undefined) {
      const { fr: topFr } = props;
      fr = topFr;
    }
    if (props.dataItem) {
      const { fr: dataItemFr = 1 } = props.dataItem;
      fr = dataItemFr;
    }

    const zeroCase = !fr;

    return `${style} minmax(${zeroCase ? '20px' : '50px'}, ${fr}fr)`;
  }, '');

  return {
    gridTemplateColumns,
  };
};

const GridHeader = ({ children, className }: BasicProps) => {
  const classList = classnames('grid-header', className);

  return <div className={classList}>{children}</div>;
};

const GridLabels = ({
  children,
  className,
  gridTemplateColumns,
}: GridLabelProps) => {
  const classList = classnames('grid-labels', className);
  const style = getGridColumnsStyle(children, gridTemplateColumns);

  return (
    <div className={classList} style={style}>
      {children}
    </div>
  );
};

const GridRow = ({
  children,
  className,
  onClick,
  gridTemplateColumns,
}: GridRowProps) => {
  const classList = classnames('grid-row', className);
  const style = getGridColumnsStyle(children, gridTemplateColumns);

  return (
    <div onClick={onClick} className={classList} style={style}>
      {children}
    </div>
  );
};

const Grid = ({ children, className }: BasicProps) => {
  const classList = classnames('grid-container', className, 'grid--data');

  return <div className={classList}>{children}</div>;
};

const GridCell = ({
  children,
  className,
  onClick,
  gridColumn,
  overflowable,
  ...props
}: GridCellProps) => {
  const classList = classnames('grid-cell', className, {
    'grid-cell--overflowable': overflowable,
  });
  const style = gridColumn ? { gridColumn } : {};

  return (
    <div style={style} onClick={onClick} className={classList} {...props}>
      {children}
    </div>
  );
};

export { Grid, GridCell, GridHeader, GridLabels, GridRow };
